import { useState, useEffect } from 'react';
import { LIFTING_CHAINS, MARKETPLACE_CHAINS } from '@shared/types/enums';
import { useLiftingStore } from '@ewf/stores/lifting';
import { useConnectionStore } from '@ewf/stores/connection';
import { useNotificationStore } from '@ewf/stores/notifications';
import { truncateBalance } from '@ewf/lib/utils';
import * as Dialog from '@ewf/components/Dialog';
import { TxExecuting } from '@ewf/components/TxExecuting';
import { TxSuccess } from '@ewf/components/TxSuccess';
import { TxFailure } from '@ewf/components/TxFailure';
import { SelectNetwork } from './SelectNetwork';
import { ConnectLiftingWallet } from './ConnectLiftingWallet/ConnectLiftingWallet';
import { Approve } from './Approve';
import { Confirm } from './Confirm';
import { useWalletEnvStore } from '@ewf/stores/wallet-env';
import { useWalletStore } from '@ewf/stores/wallets';
import { useBalanceStore } from '@ewf/stores/balance';
import { useApiServiceStore } from '@ewf/stores/api';
import { useLedgerStore, formatLedgerError } from '@ewf/stores/ledger';

type Step =
  | 'select-network'
  | 'connect-eth'
  | 'connect-ewc'
  | 'approve'
  | 'confirm'
  | 'executing'
  | 'success'
  | 'failure';

export const Lifting = ({ isOpen, setIsOpen, initialChain }: Props): JSX.Element => {
  const workerApi = useApiServiceStore.getState().api;
  const [step, setStep] = useState<Step>('select-network');
  const [network, setNetwork] = useState<LIFTING_CHAINS>(MARKETPLACE_CHAINS.ETH);
  const [amount, setAmount] = useState(0);
  const [isHWWallet, setHWWallet] = useState<boolean>(false);
  const [txn, setTxn] = useState<string | null>('');
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [fee, setFee] = useState<number>(0.0);
  const walletConst = useWalletEnvStore((state) => state.walletConst);
  const isLedger = useWalletStore((state) => state.isLedger);
  const balanceETH = useBalanceStore((state) => state.balanceETH);
  const balanceEWC = useBalanceStore((state) => state.balanceEWC);
  const disconnect = useConnectionStore((state) => state.disconnect);
  const addressEWC = useConnectionStore((state) => state.addressEWC);
  const addressEWX = useConnectionStore((state) => state.addressEWX);
  const addressETH = useConnectionStore((state) => state.addressETH);
  const publicAddressEWX = useConnectionStore((state) => state.publicAddressEWX);
  const status = useLiftingStore((state) => state.status);
  const lift = useLiftingStore((state) => state.lift);
  const reset = useLiftingStore((state) => state.reset);
  const liftingAddress = network === MARKETPLACE_CHAINS.ETH ? addressETH : addressEWC;

  // If the dialog is open and there is an initial chain, skip the select network step
  // This is used when lifting from the balance page (lift directly from EWC or ETH)
  // In case lift is opened directly without being called from the balance page, the select network step is shown
  useEffect(() => {
    if (isOpen && initialChain) {
      const newStep = initialChain === MARKETPLACE_CHAINS.ETH ? 'connect-eth' : 'connect-ewc';
      setStep(newStep);
      setNetwork(initialChain);
    } else if (isOpen && !initialChain) {
      setStep('select-network');
      setNetwork(MARKETPLACE_CHAINS.ETH);
    }
  }, [isOpen, initialChain]);

  const selectNetwork = (network: LIFTING_CHAINS) => {
    setNetwork(network);
    network === MARKETPLACE_CHAINS.ETH ? setStep('connect-eth') : setStep('connect-ewc');
  };

  const createLiftingSuccessNotification = useNotificationStore(
    (state) => state.liftingSuccessNotification,
  );
  const createLiftingErrorNotification = useNotificationStore(
    (state) => state.liftingErrorNotification,
  );

  const onClose = () => {
    setIsOpen(false);
    setTimeout(() => {
      setAmount(0);
      setTxn(null);
      setStep('select-network');
      setNetwork(MARKETPLACE_CHAINS.ETH);
      network === MARKETPLACE_CHAINS.ETH
        ? disconnect(MARKETPLACE_CHAINS.ETH)
        : disconnect(MARKETPLACE_CHAINS.EWC);
      reset();
    }, 300);
    // Delay to avoid screen flickering before animation ends
  };

  const connect = (method: 'wc' | 'ledger') => {
    setHWWallet(method === 'ledger');
    setStep('approve');
  };

  const approve = (amount: number) => {
    setAmount(amount);
    setStep('confirm');
  };

  const confirm = async () => {
    setStep('executing');

    try {
      if (isHWWallet) {
        if (isLedger()) {
          const plugged = await workerApi.isDevicePlugged();
          if (!plugged) {
            throw new Error('Please plug in or unlock your Ledger device');
          }

          const locked = await workerApi.isDeviceLocked();
          if (locked) {
            throw new Error('Please unlock ledger device');
          }
        }

        useLiftingStore.setState({ status: 'pending-confirmation' });
        const { txnHash, errorStatus } = await workerApi.deviceLifting(addressEWX, amount, network);

        if (errorStatus) {
          console.error(errorStatus);
          const errorMsg = formatLedgerError(errorStatus);
          setErrorMsg(errorMsg);
          setStep('failure');
          createLiftingErrorNotification();
          return;
        }

        setTxn(txnHash);
        setStep('success');
        createLiftingSuccessNotification(amount);
      } else
        await lift(publicAddressEWX, amount, network, async ({ status, blockHash, errorMsg }) => {
          if (status === 'success') {
            setTxn(blockHash || '');
            setStep('success');
            createLiftingSuccessNotification(amount);
            return;
          }
          setErrorMsg(errorMsg || 'An unknown error occurred');
          setStep('failure');
          createLiftingErrorNotification();
        });
    } catch (error) {
      console.error(error);
      if (error instanceof Error) {
        setErrorMsg(error.message);
      } else if (typeof error === 'object' && error?.['message']) {
        setErrorMsg(error['message']);
      } else {
        setErrorMsg('An unknown error occurred');
      }
      setStep('failure');
      createLiftingErrorNotification();
    } finally {
      await disconnect(MARKETPLACE_CHAINS.EWC);
    }
  };

  const confirmGoBack = (currentAmount: number) => {
    setAmount(currentAmount);
    setStep('approve');
  };

  useEffect(() => {
    workerApi.getFeeLifting(network).then((fee) => setFee(Number(fee))); // TODO: verify fee parsing

    useLedgerStore.subscribe((state, previousState) => {
      if (previousState.status === 'connected' && state.status === 'disconnected') {
        setStep('failure');
        setErrorMsg('Ledger device was disconnected');
      }
    });
  }, [workerApi, network]);

  const stepComponents: Record<Step, JSX.Element> = {
    'select-network': (
      <SelectNetwork defaultNetwork={network} onNext={selectNetwork} onClose={onClose} />
    ),
    'connect-eth': (
      <ConnectLiftingWallet
        network={MARKETPLACE_CHAINS.ETH}
        onConnect={connect}
        onClose={onClose}
        goBack={initialChain ? onClose : () => setStep('select-network')}
      />
    ),
    'connect-ewc': (
      <ConnectLiftingWallet
        network={MARKETPLACE_CHAINS.EWC}
        onConnect={connect}
        onClose={onClose}
        goBack={initialChain ? onClose : () => setStep('select-network')}
      />
    ),
    approve: (
      <Approve
        balance={truncateBalance(
          (network === MARKETPLACE_CHAINS.ETH ? balanceETH : balanceEWC)?.token || '0',
        )}
        initAmount={amount}
        address={liftingAddress}
        network={network}
        fee={fee}
        onApprove={approve}
        onClose={onClose}
      />
    ),
    confirm: (
      <Confirm
        fee={fee}
        amount={amount}
        address={liftingAddress}
        network={network}
        balance={network === MARKETPLACE_CHAINS.ETH ? balanceETH : balanceEWC}
        onConfirm={confirm}
        onGoBack={confirmGoBack}
        onClose={onClose}
      />
    ),
    executing: (
      <TxExecuting
        amount={amount}
        address={liftingAddress}
        operation="lifting"
        txStatus={status}
        fromChain={network}
      />
    ),
    success: (
      <TxSuccess
        operation="lifting"
        txUrl={`${network === MARKETPLACE_CHAINS.EWC ? walletConst.ewcExplorerUrl : walletConst.ethExplorerUrl}/${txn}`}
        title="Transaction scheduled"
        description="ATTENTION: Lifting operation will take approximately 30 minutes to succeed."
        onNext={onClose}
        onClose={onClose}
      />
    ),
    failure: <TxFailure operation="lifting" onClose={onClose} message={errorMsg} />,
  };

  return (
    <Dialog.Root open={isOpen}>
      <Dialog.Portal>
        <Dialog.Overlay>
          <Dialog.Content forceMount>{stepComponents[step]}</Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

interface Props {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  initialChain?: LIFTING_CHAINS;
}
